import type { Board, Cell, Pos, SignalMap } from "../types.ts";

// ── Board creation ──

export function createBoard(rows: number, cols: number): Board {
  return Array.from({ length: rows }, () =>
    Array.from({ length: cols }, (): Cell => ({ kind: "empty" }))
  );
}

export function cloneBoard(board: Board): Board {
  return board.map((row) => row.map((cell) => ({ ...cell })));
}

// ── Dimensions ──

export function boardRows(board: Board): number {
  return board.length;
}

export function boardCols(board: Board): number {
  return board[0]?.length ?? 0;
}

// ── Access ──

export function inBounds(board: Board, pos: Pos): boolean {
  return (
    pos.row >= 0 &&
    pos.row < boardRows(board) &&
    pos.col >= 0 &&
    pos.col < boardCols(board)
  );
}

export function getCell(board: Board, pos: Pos): Cell | undefined {
  return board[pos.row]?.[pos.col];
}

export function setCell(board: Board, pos: Pos, cell: Cell): Board {
  const clone = cloneBoard(board);
  clone[pos.row]![pos.col] = cell;
  return clone;
}

// ── Signal map ──

export function createSignalMap(rows: number, cols: number): SignalMap {
  return Array.from({ length: rows }, () => Array(cols).fill(0) as number[]);
}

export function getSignal(map: SignalMap, pos: Pos): number {
  return map[pos.row]?.[pos.col] ?? 0;
}

export function incrementSignal(map: SignalMap, pos: Pos): void {
  if (map[pos.row] !== undefined) {
    map[pos.row]![pos.col] = (map[pos.row]![pos.col] ?? 0) + 1;
  }
}

// ── Neighbors ──

export function getNeighborPos(pos: Pos, dir: "up" | "down" | "left" | "right"): Pos {
  switch (dir) {
    case "up": return { row: pos.row - 1, col: pos.col };
    case "down": return { row: pos.row + 1, col: pos.col };
    case "left": return { row: pos.row, col: pos.col - 1 };
    case "right": return { row: pos.row, col: pos.col + 1 };
  }
}
